import { CloseIcon } from '@chakra-ui/icons'
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SystemFeature } from '@/modules/releaseNotes/domain/models/enums/SystemFeature'
import { ReleaseNotesList } from '@/modules/releaseNotes/domain/models/ReleaseNotes'
import Markdown from '@/ui/components/Markdown'

import FeatureChangeIcon from './components/FeatureChangeIcon'
import SystemFeatureIcon from './components/SystemFeatureIcon'

export default function ReleaseNotes({
  isOpen,
  onClose,
  data
}: {
  isOpen: boolean
  onClose: () => void
  data: ReleaseNotesList
}) {
  const { t } = useTranslation()

  const [releaseNotesIndex, setReleaseNotesIndex] = useState(0)
  const [canPressNext, setCanPressNext] = useState(false)

  const {
    versionMajorNumber,
    versionMinorNumber,
    description,
    videoUrl,
    changes
  } = data[releaseNotesIndex]

  useEffect(() => {
    setCanPressNext(false)
    const timer = setTimeout(() => {
      setCanPressNext(true)
    }, 500)

    return () => clearTimeout(timer)
  }, [releaseNotesIndex])

  const groupedChanges = changes.reduce<Record<SystemFeature, typeof changes>>(
    (acc, change) => {
      if (!acc[change.feature]) acc[change.feature] = []
      acc[change.feature].push(change)
      return acc
    },
    {} as Record<SystemFeature, typeof changes>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      closeOnEsc
      size={'xl'}
      scrollBehavior={'inside'}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex justifyContent={'space-between'} alignItems={'center'}>
            <Image
              src="/images/two-columns-logo.svg"
              alt={t('general.alt.home')}
              h={'32px'}
            />
            <IconButton
              colorScheme="white"
              aria-label="Close"
              icon={<CloseIcon textColor={'#D5474C'} />}
              onClick={onClose}
            />
          </Flex>
        </ModalHeader>
        <ModalBody p={0} placeItems={'center'}>
          <Box w={'95%'} position="relative">
            <Image
              w={'full'}
              src="/images/release-notes.svg"
              backgroundSize="cover"
              borderRadius="4px"
            />
            <Flex
              position="absolute"
              fontWeight="700"
              color="#F9FCFF"
              fontSize="39px"
              top={'5'}
              left={'5'}
              flexDir={'column'}
            >
              <Text>{t('releaseNotes.newUpdate')}</Text>
              <Text>
                {versionMajorNumber}.{versionMinorNumber}
              </Text>
            </Flex>
          </Box>
          {videoUrl && (
            <Box
              mb={4}
              mt={2}
              w={'95%'}
              h={'100%'}
              borderRadius="4px"
              overflow="hidden"
            >
              <video
                controlsList={'nodownload'}
                controls
                width={'100%'}
                height={'100%'}
              >
                <source src={videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </Box>
          )}
          {description && (
            <Markdown
              value={description}
              padding="0px 24px 0px 24px"
              mentionKey=""
              isReadonly
            />
          )}
          {Object.entries(groupedChanges).map(([feature, changes]) => {
            const featureEnum = feature as SystemFeature

            return (
              <Accordion key={feature} allowMultiple mt={4} w={'95%'}>
                <AccordionItem>
                  <AccordionButton>
                    <Flex gap={2} w={'full'} alignItems={'center'}>
                      <SystemFeatureIcon systemFeature={featureEnum} />
                      {t(
                        `releaseNotes.systemFeatures.${
                          featureEnum.toLowerCase() as Lowercase<
                            typeof featureEnum
                          >
                        }`
                      )}
                    </Flex>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <Accordion allowToggle>
                      {changes.map(({ id, description, changeType }, index) => (
                        <Flex
                          key={id}
                          gap={2}
                          mb={index === changes.length - 1 ? 0 : 2}
                        >
                          <FeatureChangeIcon changeType={changeType} />
                          <Text>{description}</Text>
                        </Flex>
                      ))}
                    </Accordion>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            )
          })}
        </ModalBody>
        <ModalFooter>
          <Flex flexDir={'column'} w={'full'} rowGap={'4'}>
            <Button
              colorScheme={'blue'}
              isDisabled={!canPressNext}
              type={'submit'}
              onClick={() => {
                if (releaseNotesIndex === data.length - 1) {
                  onClose()
                  return
                }

                setReleaseNotesIndex(releaseNotesIndex + 1)
              }}
            >
              {t(
                `releaseNotes.${
                  releaseNotesIndex === data.length - 1 ? 'continue' : 'next'
                }`
              )}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
